// React
import React from 'react';
// redux hooks
import { useFieldArray, Controller } from 'react-hook-form';
// BASE components
import BASESvgIcon from '../../../../../../../@BASE/core/BASESvgIcon';
import BASEUtils from '../../../../../../../@BASE/utils/BASEUtils';
// material-ui components
import Accordion from '@mui/material/Accordion';
import AccordionActions from '@mui/material/AccordionActions';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/system/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';

// custom components
import NumberedHeading from '../../../../../../shared-components/forms/NumberedHeading';
// data
import { ReviewData } from '../../../data/Defaults';

export default function LessonFeedbackArray({
  control,
  register,
  setValue,
  getValues,
  errors,
  inputDir,
  materialPages
}) {
  // setup field array
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'content.item_feedback'
  });
  if (materialPages === null) {
    return;
  }

  return (
    <>
      <Timeline
        sx={{
          [`& .${timelineItemClasses.root}:before`]: {
            flex: 0,
            padding: 0
          }
        }}
        className="px-0"
      >
        {fields.map((item, index) => {
          return (
            <TimelineItem key={item.id}>
              <TimelineSeparator>
                <TimelineDot color="secondary" sx={{ marginTop: '22px' }} />
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent className="py-0">
                <Accordion
                  defaultExpanded
                  className="bg-transparent shadow-none"
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`${index}`}
                    id={`${index}`}
                    className="py-0 px-16 m-0 rounded bg-white shadow-sm"
                    sx={{
                      border: 'solid 1px',
                      borderColor: '#bdbdbd'
                    }}
                  >
                    <Typography className="grow m-0 font-semibold">
                      Feedback Item {index + 1}
                    </Typography>
                    {index !== 0 && (
                      <IconButton
                        className="ml-16 mr-8"
                        aria-label="move up"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          move(index, index - 1);
                        }}
                      >
                        <BASESvgIcon size={20}>
                          heroicons-solid:arrow-circle-up
                        </BASESvgIcon>
                      </IconButton>
                    )}
                    {index !== fields.length - 1 && (
                      <IconButton
                        className="mx-8"
                        aria-label="move down"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          move(index, index + 1);
                        }}
                      >
                        <BASESvgIcon size={20}>
                          heroicons-solid:arrow-circle-down
                        </BASESvgIcon>
                      </IconButton>
                    )}
                    <IconButton
                      className="ml-8 mr-16"
                      aria-label="delete"
                      color="error"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(index);
                      }}
                    >
                      <BASESvgIcon size={20}>
                        heroicons-outline:trash
                      </BASESvgIcon>
                    </IconButton>
                  </AccordionSummary>
                  <AccordionDetails className="p-0">
                    <Box className="p-0">
                      <NumberedHeading
                        number="1"
                        title="Activity Title (TL)"
                        instructions="Enter the title of this activity in the target language."
                      />
                      <Controller
                        control={control}
                        name={`content.item_feedback.${index}.title_tl`}
                        render={({ field }) => (
                          <>
                            <TextField
                              {...field}
                              className="mt-16 mb-16"
                              label="Title"
                              placeholder="Title"
                              variant="outlined"
                              fullWidth
                              error={
                                !!errors.content?.item_feedback?.[index]
                                  ?.title_tl
                              }
                              helperText={
                                errors.content?.item_feedback?.[index]?.title_tl
                                  ?.message
                              }
                              required
                              dir={inputDir}
                            />
                          </>
                        )}
                      />

                      <NumberedHeading
                        number="2"
                        title="Activity Title (EN)"
                        instructions="Enter the title of this activity in English."
                      />
                      <Controller
                        control={control}
                        name={`content.item_feedback.${index}.title_en`}
                        render={({ field }) => (
                          <>
                            <TextField
                              {...field}
                              className="mt-16 mb-16"
                              label="Title"
                              placeholder="Title"
                              variant="outlined"
                              fullWidth
                              error={
                                !!errors.content?.item_feedback?.[index]
                                  ?.title_en
                              }
                              helperText={
                                errors.content?.item_feedback?.[index]?.title_en
                                  ?.message
                              }
                              required
                            />
                          </>
                        )}
                      />

                      <NumberedHeading
                        number="3"
                        title="Interaction Type"
                        instructions='Select either "Lesson", "Practical Exercise", or "Final Practical Exercise".'
                        required
                      />
                      <Controller
                        control={control}
                        name={`content.item_feedback.${index}.interaction_type`}
                        render={({ field }) => (
                          <FormControl
                            fullWidth
                            className="mt-16"
                            size="small"
                            error={
                              !!errors.content?.item_feedback?.[index]
                                ?.interaction_type
                            }
                            required
                          >
                            <InputLabel id="select-type">
                              Interaction Type
                            </InputLabel>
                            <Select
                              {...field}
                              labelId="select-type"
                              id="type"
                              label="Interaction Type"
                              classes={{
                                select: 'flex items-center space-x-12'
                              }}
                              defaultValue=""
                            >
                              <MenuItem
                                value=""
                                className="space-x-12"
                                disabled
                              >
                                Select an Interaction Type
                              </MenuItem>
                              <MenuItem value="0" className="space-x-12">
                                Reading Comprehension
                              </MenuItem>
                              <MenuItem value="1" className="space-x-12">
                                Listening Comprehension
                              </MenuItem>
                              <MenuItem value="2" className="space-x-12">
                                Statement Completion
                              </MenuItem>
                              <MenuItem value="3" className="space-x-12">
                                Translation Analysis
                              </MenuItem>
                              <MenuItem value="4" className="space-x-12">
                                Skill Integration: Multiple Choice
                              </MenuItem>
                              <MenuItem value="5" className="space-x-12">
                                Question Selection
                              </MenuItem>
                              <MenuItem value="6" className="space-x-12">
                                Concept Categorizing
                              </MenuItem>
                              <MenuItem value="7" className="space-x-12">
                                Error Identification
                              </MenuItem>
                            </Select>
                            {!!errors.content?.item_feedback?.[index]
                              ?.interaction_type ? (
                              <FormHelperText>
                                {
                                  errors.content?.item_feedback?.[index]
                                    ?.interaction_type?.message
                                }
                              </FormHelperText>
                            ) : null}
                          </FormControl>
                        )}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading
                        number="4"
                        title="Page/Interaction ID"
                        instructions="Enter the page ID associated with this feedback item. This should be filled out by the development team."
                      />
                      <Controller
                        control={control}
                        name={`content.item_feedback.${index}.interaction_id`}
                        render={({ field }) => (
                          <FormControl
                            className="mt-16"
                            fullWidth
                            size="small"
                            error={
                              !!errors.content?.item_feedback?.[index]
                                ?.interaction_id
                            }
                          >
                            <InputLabel id={`media-page-select-label-${index}`}>
                              Media Page
                            </InputLabel>
                            <Select
                              labelId={`media-page-select-label-${index}`}
                              id={`media-page-select-${index}`}
                              {...field}
                              label="Media Page"
                              value={
                                materialPages.find(
                                  (page) => page.id === field.value
                                )
                                  ? field.value
                                  : ''
                              }
                            >
                              <MenuItem value="" disabled>
                                <em>Select a media page</em>
                              </MenuItem>
                              {materialPages.map((page) => (
                                <MenuItem key={page.id} value={page.id}>
                                  {page.label} - {page.title}
                                </MenuItem>
                              ))}
                            </Select>
                            <FormHelperText>
                              {
                                errors.content?.item_feedback?.[index]
                                  ?.interaction_id?.message
                              }
                            </FormHelperText>
                          </FormControl>
                        )}
                      />
                      <Divider className="mt-16" />
                    </Box>
                  </AccordionDetails>
                  <AccordionActions>
                    {index !== 0 && (
                      <Button
                        variant="outlined"
                        onClick={() => move(index, index - 1)}
                        startIcon={
                          <BASESvgIcon size={20}>
                            heroicons-solid:arrow-circle-up
                          </BASESvgIcon>
                        }
                        size="small"
                      >
                        Move Up
                      </Button>
                    )}
                    {index !== fields.length - 1 && (
                      <Button
                        variant="outlined"
                        onClick={() => move(index, index + 1)}
                        startIcon={
                          <BASESvgIcon size={20}>
                            heroicons-solid:arrow-circle-down
                          </BASESvgIcon>
                        }
                        size="small"
                      >
                        Move Down
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      onClick={() => remove(index)}
                      color="error"
                      startIcon={
                        <BASESvgIcon size={20}>
                          heroicons-outline:trash
                        </BASESvgIcon>
                      }
                      size="small"
                    >
                      Remove
                    </Button>
                  </AccordionActions>
                </Accordion>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
      <Box className="flex items-center mt-0 p-0">
        <Button
          className="group flex items-center mt-8 rounded cursor-pointer"
          color="secondary"
          variant="contained"
          onClick={() =>
            append({
              id: BASEUtils.generateGUID(),
              review: ReviewData,
              title_en: '',
              title_tl: '',
              interaction_type: '',
              interaction_id: ''
            })
          }
        >
          <BASESvgIcon size={20}>heroicons-solid:plus-circle</BASESvgIcon>
          <span className="ml-8 font-medium text-secondary group-hover:underline">
            Add Item
          </span>
        </Button>
      </Box>
    </>
  );
}
